/**
 * JWT 토큰 자동 갱신 서비스
 * 
 * 기능:
 * 1. 토큰 만료 시간 추적
 * 2. 만료 15일 전부터 자동 갱신
 * 3. 갱신 실패 시 재시도 (3회)
 * 4. 로그아웃 시 갱신 중지
 * 
 * 설정:
 * - 토큰 유효 기간: 1달(30일)
 * - 갱신 시작: 만료 15일 전부터
 * - 갱신 간격: 24시간마다 (성공 시)
 * - 체크 주기: 12시간마다
 */

import { getSafeApiUrl } from '../config/environment';

class TokenRefreshService {
  private refreshTimer: NodeJS.Timeout | null = null;
  private isRefreshing: boolean = false;
  private readonly REFRESH_BEFORE_EXPIRY = 15 * 24 * 60 * 60 * 1000; // 15일 전부터 (밀리초)
  private readonly CHECK_INTERVAL = 12 * 60 * 60 * 1000; // 12시간마다 체크
  private readonly MAX_RETRY = 3;
  private retryCount: number = 0;
  private lastSuccessfulRefresh: number = 0; // 마지막 성공적인 갱신 시간

  /**
   * 토큰 자동 갱신 시작
   */
  public startAutoRefresh(): void {
    console.log('🔄 [토큰 갱신] 자동 갱신 서비스 시작');
    
    // 기존 타이머 정리
    this.stopAutoRefresh();
    
    // 즉시 토큰 상태 확인
    this.checkAndRefreshToken();
    
    // 주기적으로 토큰 상태 확인 (12시간마다)
    this.refreshTimer = setInterval(() => {
      this.checkAndRefreshToken();
    }, this.CHECK_INTERVAL);
  }

  /**
   * 토큰 자동 갱신 중지
   */
  public stopAutoRefresh(): void {
    if (this.refreshTimer) {
      console.log('🛑 [토큰 갱신] 자동 갱신 서비스 중지');
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.isRefreshing = false;
    this.retryCount = 0;
    this.lastSuccessfulRefresh = 0; // 갱신 시간 초기화
  }

  /**
   * 토큰 만료 시간 확인 및 필요 시 갱신
   */
  private async checkAndRefreshToken(): Promise<void> {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.log('⚠️ [토큰 갱신] 토큰이 없음 - 자동 갱신 중지');
        this.stopAutoRefresh();
        return;
      }

      // JWT 토큰에서 만료 시간 추출
      const expirationTime = this.getTokenExpirationTime(token);
      
      if (!expirationTime) {
        console.warn('⚠️ [토큰 갱신] 토큰 만료 시간을 확인할 수 없음');
        return;
      }

      const now = Date.now();
      const timeUntilExpiry = expirationTime - now;
      const daysUntilExpiry = Math.floor(timeUntilExpiry / 1000 / 60 / 60 / 24);
      const hoursUntilExpiry = Math.floor((timeUntilExpiry / 1000 / 60 / 60) % 24);

      console.log(`⏰ [토큰 갱신] 토큰 만료까지 ${daysUntilExpiry}일 ${hoursUntilExpiry}시간 남음`);

      // 만료 15일 전부터 갱신 시도
      if (timeUntilExpiry <= this.REFRESH_BEFORE_EXPIRY) {
        // 24시간 이내에 이미 갱신 성공했으면 건너뛰기 (과도한 갱신 방지)
        const timeSinceLastRefresh = now - this.lastSuccessfulRefresh;
        const MIN_REFRESH_INTERVAL = 24 * 60 * 60 * 1000; // 24시간
        
        if (this.lastSuccessfulRefresh > 0 && timeSinceLastRefresh < MIN_REFRESH_INTERVAL) {
          const hoursAgo = Math.floor(timeSinceLastRefresh / 1000 / 60 / 60);
          console.log(`✅ [토큰 갱신] ${hoursAgo}시간 전에 갱신 성공함 - 건너뛰기`);
          return;
        }
        
        // 토큰이 너무 새로우면 (1시간 이내) 갱신 건너뛰기 (로그인 직후 방지)
        const MIN_TOKEN_AGE = 60 * 60 * 1000; // 1시간
        const tokenAge = now - (expirationTime - timeUntilExpiry);
        if (tokenAge < MIN_TOKEN_AGE) {
          const minutesAgo = Math.floor(tokenAge / 1000 / 60);
          console.log(`✅ [토큰 갱신] 토큰이 너무 새로움 (${minutesAgo}분 전 발급) - 갱신 건너뛰기`);
          return;
        }
        
        console.log(`🔄 [토큰 갱신] 토큰 갱신 필요 (만료까지 ${daysUntilExpiry}일) - 갱신 시작`);
        const success = await this.refreshToken();
        
        if (success) {
          this.lastSuccessfulRefresh = now; // 성공 시 시간 기록
          console.log('✅ [토큰 갱신] 갱신 성공 - 다음 갱신까지 24시간 대기');
        }
      } else {
        console.log(`✅ [토큰 갱신] 토큰이 아직 유효함 (만료까지 ${daysUntilExpiry}일 ${hoursUntilExpiry}시간)`);
      }
    } catch (error) {
      console.error('❌ [토큰 갱신] 토큰 확인 중 오류:', error);
    }
  }

  /**
   * 토큰 갱신 실행
   */
  private async refreshToken(): Promise<boolean> {
    // 이미 갱신 중이면 건너뛰기
    if (this.isRefreshing) {
      console.log('⏳ [토큰 갱신] 이미 갱신 중 - 건너뛰기');
      return false;
    }

    this.isRefreshing = true;

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.warn('⚠️ [토큰 갱신] 토큰이 없음');
        this.isRefreshing = false;
        return false;
      }

      console.log('📡 [토큰 갱신] 서버에 토큰 갱신 요청 중...');

      // API Gateway를 통해 Identity 서비스의 refresh-token 엔드포인트 호출
      const API_BASE_URL = getSafeApiUrl('GATEWAY');
      console.log('🔍 [토큰 갱신] API URL:', API_BASE_URL);
      
      const response = await fetch(`${API_BASE_URL}/stores/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // 새 토큰 저장
        localStorage.setItem('token', data.token);
        
        // 사용자 정보 업데이트 (선택적)
        if (data.user) {
          localStorage.setItem('currentUser', JSON.stringify(data.user));
          console.log('✅ [토큰 갱신] 사용자 정보 업데이트 완료');
        }

        console.log('✅ [토큰 갱신] 토큰 갱신 성공');
        this.retryCount = 0; // 재시도 카운트 초기화
        this.isRefreshing = false;
        return true;
      } else {
        // 405 Method Not Allowed는 백엔드 API 미구현으로 무시
        if (response.status === 405) {
          console.warn('⚠️ [토큰 갱신] API 미구현 (405) - 토큰 갱신 건너뛰기');
          this.isRefreshing = false;
          return false;
        }
        
        console.error('❌ [토큰 갱신] 서버 응답 실패:', response.status, response.statusText);
        
        // 401 Unauthorized이면 로그아웃 (단, 로그인 직후가 아닌 경우에만)
        if (response.status === 401) {
          // 토큰이 너무 새로우면 (1시간 이내) 로그아웃하지 않고 건너뛰기
          const token = localStorage.getItem('token');
          if (token) {
            const expirationTime = this.getTokenExpirationTime(token);
            if (expirationTime) {
              const now = Date.now();
              const timeUntilExpiry = expirationTime - now;
              const tokenAge = now - (expirationTime - timeUntilExpiry);
              const MIN_TOKEN_AGE = 60 * 60 * 1000; // 1시간
              
              if (tokenAge < MIN_TOKEN_AGE) {
                console.warn('⚠️ [토큰 갱신] 로그인 직후 갱신 실패 - 건너뛰기 (로그아웃 안함)');
                this.isRefreshing = false;
                return false;
              }
            }
          }
          
          console.warn('⚠️ [토큰 갱신] 인증 실패 - 로그아웃 처리');
          this.handleLogout();
          this.isRefreshing = false;
          return false;
        }

        // 재시도 (즉시 3회만, 이후는 다음 체크 시간까지 대기)
        if (this.retryCount < this.MAX_RETRY) {
          this.retryCount++;
          console.log(`🔄 [토큰 갱신] 즉시 재시도 ${this.retryCount}/${this.MAX_RETRY}`);
          this.isRefreshing = false;
          
          // 5초 후 재시도
          await new Promise(resolve => setTimeout(resolve, 5000));
          return await this.refreshToken();
        } else {
          // 즉시 재시도 3회 실패 → 다음 체크 시간까지 대기 (로그아웃 안함!)
          console.warn(`⚠️ [토큰 갱신] 즉시 재시도 ${this.MAX_RETRY}회 실패 - 다음 체크 시간에 재시도`);
          this.retryCount = 0; // 재시도 카운트 초기화 (다음 체크를 위해)
          this.isRefreshing = false;
          return false;
        }
      }
    } catch (error) {
      console.error('❌ [토큰 갱신] 네트워크 오류:', error);
      
      // 재시도 (즉시 3회만, 이후는 다음 체크 시간까지 대기)
      if (this.retryCount < this.MAX_RETRY) {
        this.retryCount++;
        console.log(`🔄 [토큰 갱신] 즉시 재시도 ${this.retryCount}/${this.MAX_RETRY}`);
        this.isRefreshing = false;
        
        // 5초 후 재시도
        await new Promise(resolve => setTimeout(resolve, 5000));
        return await this.refreshToken();
      } else {
        // 즉시 재시도 3회 실패 → 다음 체크 시간까지 대기 (로그아웃 안함!)
        console.warn(`⚠️ [토큰 갱신] 즉시 재시도 ${this.MAX_RETRY}회 실패 - 다음 체크 시간에 재시도`);
        this.retryCount = 0; // 재시도 카운트 초기화 (다음 체크를 위해)
        this.isRefreshing = false;
        return false;
      }
    }
  }

  /**
   * JWT 토큰에서 만료 시간 추출
   */
  private getTokenExpirationTime(token: string): number | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      const payload = JSON.parse(atob(parts[1]));
      
      if (payload.exp) {
        // JWT exp는 초 단위이므로 밀리초로 변환
        return payload.exp * 1000;
      }

      return null;
    } catch (error) {
      console.error('❌ [토큰 갱신] 토큰 파싱 오류:', error);
      return null;
    }
  }

  /**
   * 로그아웃 처리
   */
  private handleLogout(): void {
    console.log('🚪 [토큰 갱신] 로그아웃 처리');
    
    // 자동 갱신 중지
    this.stopAutoRefresh();
    
    // 토큰 및 사용자 정보 삭제
    localStorage.removeItem('token');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('storeId');
    
    // 로그인 페이지로 리디렉션
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
  }

  /**
   * 수동 토큰 갱신 (사용자 액션 시)
   */
  public async manualRefresh(): Promise<boolean> {
    console.log('🔄 [토큰 갱신] 수동 갱신 요청');
    return await this.refreshToken();
  }
}

// 싱글톤 인스턴스 생성 및 내보내기
const tokenRefreshService = new TokenRefreshService();
export default tokenRefreshService;

